/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import { observable, action, computed } from 'mobx'
import { injectable } from 'inversify'
import { injectProperty } from '../../arc/configuration/ServiceLocator'
import { ICoverageDiscoveryRequestStateManager, ICoverageDiscoveryRequestDetail, IStates, ICoverageDiscoveryResponse } from './contracts'
import { ValidationResult } from '../../arc/utils/ValidationResult/ValidationResult'
import isEmpty from 'validator/lib/isEmpty'
import isLength from 'validator/lib/isLength'
import { ICoverageDiscoveryRequestApiClient } from '../CoverageDiscoveryRequestApiClient/contracts'
import { IAccountFacilityApiClient } from '../AccountFacilityApiClient/contracts'
import { INpi } from '../RequestHistoryStateManager/contracts'
import { SettingsManager } from '../../configuration/SettingsManager'

@injectable()
export class CoverageDiscoveryRequestStateManager implements ICoverageDiscoveryRequestStateManager {
	@observable
	selectedRequestDetails: ICoverageDiscoveryRequestDetail

	@observable
	selectedRequestDetail: ICoverageDiscoveryRequestDetail

	@observable
	availableFacilities: INpi[]

	@observable
	availableAccountFacilities: INpi[]

	@observable
	availableStates: IStates[]

	@observable
	isResponseAvailable: boolean

	@observable
	isCoverageDiscoveryStatusUpdated: boolean

	@injectProperty('ICoverageDiscoveryRequestApiClient')
	public CoverageDiscoveryRequestApiClient!: ICoverageDiscoveryRequestApiClient
	@injectProperty('IAccountFacilityApiClient')
	public AccountFacilityApiClient!: IAccountFacilityApiClient

	constructor() {
		this.selectedRequestDetail = this.getBlankItem()
		this.availableFacilities = []
		this.availableStates = []
		this.selectedRequestDetails = this.getBlankCoverageDiscoveryRequestItem()
		this.availableAccountFacilities = []
		this.isResponseAvailable = false
		this.isCoverageDiscoveryStatusUpdated = false
	}
	getBlankItem(): ICoverageDiscoveryRequestDetail {
		return {
			id: 0,
			customerEntityFacilityId: 0,
			RequestedByUserId: null,
			serviceBeginDate: new Date().toDateString(),
			serviceEndDate: new Date().toDateString(),
			individualLastName: '',
			individualMiddleName: '',
			individualFirstName: '',
			individualGender: '',
			individualDateOfBirth: null,
			individualSocialSecurityNumber: null,
			individualAddress1: null,
			individualAddress2: null,
			individualCity: null,
			individualStateCodeId: null,
			individualZip: null,
			individualPhoneNumber: null,
			guarantorLastName: null,
			guarantorMiddleName: null,
			guarantorFirstName: null,
			guarantorGender: null,
			guarantorDateOfBirth: null,
			guarantorSocialSecurityNumber: null,
			guarantorAddress1: null,
			guarantorAddress2: null,
			guarantorCity: null,
			guarantorStateCodeId: null,
			guarantorZip: null,
			guarantorPhoneNumber: null,
		}
	}
	getBlankCoverageDiscoveryRequestItem(): ICoverageDiscoveryRequestDetail {
		return this.selectedRequestDetail
	}
	@computed get validateSelectedGuarantorDetail(): ValidationResult {
		const result = new ValidationResult()
		const nameRegex = new RegExp(SettingsManager.REACT_APP_TEXT_FIELD_VALIDATATION_REGEX().value, 'g')
		if (this.selectedRequestDetail !== null && typeof this.selectedRequestDetail !== 'undefined') {
			if (this.selectedRequestDetail.guarantorFirstName === null || isEmpty(this.selectedRequestDetail.guarantorFirstName.trim())) {
				result.setMessage('guarantorFirstName', 'First Name is required.')
			}
			if (this.selectedRequestDetail.guarantorFirstName !== null && !isEmpty(this.selectedRequestDetail.guarantorFirstName) && this.selectedRequestDetail.guarantorFirstName.length > 200) {
				result.setMessage('guarantorFirstName', 'Max length is 200 characters.')
			}
			if (this.selectedRequestDetail.guarantorFirstName !== null && !isEmpty(this.selectedRequestDetail.guarantorFirstName) && this.selectedRequestDetail.guarantorFirstName.match(nameRegex)) {
				result.setMessage('guarantorFirstName', 'Please enter a valid name.')
			}
			if (this.selectedRequestDetail.guarantorLastName === null || isEmpty(this.selectedRequestDetail.guarantorLastName.trim())) {
				result.setMessage('guarantorLastName', 'Last Name is required.')
			}
			if (this.selectedRequestDetail.guarantorLastName !== null && !isEmpty(this.selectedRequestDetail.guarantorLastName) && this.selectedRequestDetail.guarantorLastName.length > 200) {
				result.setMessage('guarantorLastName', 'Max length is 200 characters.')
			}
			if (this.selectedRequestDetail.guarantorLastName !== null && !isEmpty(this.selectedRequestDetail.guarantorLastName) && this.selectedRequestDetail.guarantorLastName.match(nameRegex)) {
				result.setMessage('guarantorLastName', 'Please enter a valid name.')
			}
			if (this.selectedRequestDetail.guarantorMiddleName !== null && !isEmpty(this.selectedRequestDetail.guarantorMiddleName.trim()) && this.selectedRequestDetail.guarantorMiddleName.length > 200) {
				result.setMessage('guarantorMiddleName', 'Max length is 200 characters.')
			}
			if (this.selectedRequestDetail.guarantorMiddleName !== null && !isEmpty(this.selectedRequestDetail.guarantorMiddleName) && this.selectedRequestDetail.guarantorMiddleName.match(nameRegex)) {
				result.setMessage('guarantorMiddleName', 'Please enter a valid name.')
			}
			if (this.selectedRequestDetail.guarantorCity !== null && !isEmpty(this.selectedRequestDetail.guarantorCity)) {
				if (this.selectedRequestDetail.guarantorCity.match(nameRegex)) {
					result.setMessage('guarantorCity', 'Please enter a valid city.')
				} else if (this.selectedRequestDetail.guarantorCity.length > 28) {
					result.setMessage('guarantorCity', 'Max length is 28 characters.')
				}
			}
			if (this.selectedRequestDetail.guarantorPhoneNumber !== null && !isEmpty(this.selectedRequestDetail.guarantorPhoneNumber) && !this.selectedRequestDetail.guarantorPhoneNumber.match(/^[0-9]*$/)) {
				result.setMessage('guarantorPhoneNumber', 'Valid HomePhone must be non-whitespace')
			}
			if (this.selectedRequestDetail.guarantorPhoneNumber !== null && !isEmpty(this.selectedRequestDetail.guarantorPhoneNumber) && !isLength(this.selectedRequestDetail.guarantorPhoneNumber, { min: 10, max: 10 })) {
				result.setMessage('guarantorPhoneNumber', 'Valid HomePhone must be exactly 10 numbers.')
			}
			if (this.selectedRequestDetail.guarantorAddress1 !== null && !isEmpty(this.selectedRequestDetail.guarantorAddress1)) {
				if (this.selectedRequestDetail.guarantorAddress1.match(nameRegex)) {
					result.setMessage('guarantorAddress1', 'Please enter a valid address.')
				} else if (this.selectedRequestDetail.guarantorAddress1.length > 200) {
					result.setMessage('guarantorAddress1', 'Max length is 200 characters.')
				}
			}
			if (this.selectedRequestDetail.guarantorAddress2 !== null && !isEmpty(this.selectedRequestDetail.guarantorAddress2)) {
				if (this.selectedRequestDetail.guarantorAddress2.match(nameRegex)) {
					result.setMessage('guarantorAddress2', 'Please enter a valid address.')
				} else if (this.selectedRequestDetail.guarantorAddress2.length > 200) {
					result.setMessage('guarantorAddress2', 'Max length is 200 characters.')
				}
			}

			if (this.selectedRequestDetail.guarantorSocialSecurityNumber === null || isEmpty(this.selectedRequestDetail.guarantorSocialSecurityNumber.trim())) {
				if (this.selectedRequestDetail.guarantorDateOfBirth === null || new Date(this.selectedRequestDetail.guarantorDateOfBirth) > new Date()) {
					result.setMessage('guarantorSocialSecurityNumber', 'Date Of Birth or Social Security Number is required')
					result.setMessage('guarantorDateOfBirth', 'Date Of Birth or Social Security Number is required')
				}
			}
			if (this.selectedRequestDetail.guarantorDateOfBirth === null) {
				if (this.selectedRequestDetail.guarantorSocialSecurityNumber === null || isEmpty(this.selectedRequestDetail.guarantorSocialSecurityNumber.trim())) {
					result.setMessage('guarantorDateOfBirth', 'Date Of Birth or Social Security Number is required')
				}
			}

			if (this.selectedRequestDetail.guarantorDateOfBirth !== null && new Date(this.selectedRequestDetail.guarantorDateOfBirth) > new Date()) {
				result.setMessage('guarantorDateOfBirth', 'Date of Birth value cannot be a date in the future.')
			}

			if (this.selectedRequestDetail.guarantorZip !== null && !isEmpty(this.selectedRequestDetail.guarantorZip) && !isLength(this.selectedRequestDetail.guarantorZip, { min: 5, max: 5 }) && !isLength(this.selectedRequestDetail.guarantorZip, { min: 9, max: 10 })) {
				result.setMessage('guarantorZip', 'Valid ZIP Code must be either 5 or 9 characters long.')
			}
			if (this.selectedRequestDetail.guarantorZip !== null && !isEmpty(this.selectedRequestDetail.guarantorZip) && !this.selectedRequestDetail.guarantorZip.match(/^(?:\d{5}(?:[-]\d{4})?|\d{9})$/)) {
				result.setMessage('guarantorZip', 'Valid ZIP Code should contain only numbers and hypen.')
			}
			if (this.selectedRequestDetail.guarantorSocialSecurityNumber !== null && !isEmpty(this.selectedRequestDetail.guarantorSocialSecurityNumber)) {
				if (!this.selectedRequestDetail.guarantorSocialSecurityNumber.match(/\b[0-9]{3}[-][0-9]{2}[-][0-9]{4}\b/) && !this.selectedRequestDetail.guarantorSocialSecurityNumber.match(/\b[0-9]{9}\b/)) {
					if (!this.selectedRequestDetail.guarantorSocialSecurityNumber!.match(/\b^[0-9]{4}$\b/)) {
						result.setMessage('guarantorSocialSecurityNumber', 'Valid SSN should contain either 9 numbers and/or 2 hypens.')
						result.setMessage('guarantorDateOfBirth', 'Date Of Birth or Social Security Number is required')
					} else {
						if (this.selectedRequestDetail.guarantorDateOfBirth === null) result.setMessage('guarantorDateOfBirth', 'Provided SSN is partial, so Date of Birth is also required')
					}
				}
			}
		}
		return result
	}
	@computed get validateSelectedRequestDetail(): ValidationResult {
		const result = new ValidationResult()
		const nameRegex = new RegExp(SettingsManager.REACT_APP_TEXT_FIELD_VALIDATATION_REGEX().value, 'g')
		if (this.selectedRequestDetail !== null && typeof this.selectedRequestDetail !== 'undefined') {
			if (this.selectedRequestDetail.individualFirstName === null || isEmpty(this.selectedRequestDetail.individualFirstName.trim())) {
				result.setMessage('individualFirstName', 'First Name is required.')
			}
			if (this.selectedRequestDetail.individualFirstName !== null && !isEmpty(this.selectedRequestDetail.individualFirstName) && this.selectedRequestDetail.individualFirstName.length > 200) {
				result.setMessage('individualFirstName', 'Max length is 200 characters.')
			}
			if (this.selectedRequestDetail.individualFirstName !== null && !isEmpty(this.selectedRequestDetail.individualFirstName) && this.selectedRequestDetail.individualFirstName.match(nameRegex)) {
				result.setMessage('individualFirstName', 'Please enter a valid name.')
			}
			if (this.selectedRequestDetail.individualLastName === null || isEmpty(this.selectedRequestDetail.individualLastName.trim())) {
				result.setMessage('individualLastName', 'Last Name is required.')
			}
			if (this.selectedRequestDetail.individualLastName !== null && !isEmpty(this.selectedRequestDetail.individualLastName) && this.selectedRequestDetail.individualLastName.length > 200) {
				result.setMessage('individualLastName', 'Max length is 200 characters.')
			}
			if (this.selectedRequestDetail.individualLastName !== null && !isEmpty(this.selectedRequestDetail.individualLastName) && this.selectedRequestDetail.individualLastName.match(nameRegex)) {
				result.setMessage('individualLastName', 'Please enter a valid name.')
			}
			if (this.selectedRequestDetail.individualMiddleName !== null && !isEmpty(this.selectedRequestDetail.individualMiddleName.trim()) && this.selectedRequestDetail.individualMiddleName.length > 200) {
				result.setMessage('individualMiddleName', 'Max length is 200 characters.')
			}
			if (this.selectedRequestDetail.individualMiddleName !== null && !isEmpty(this.selectedRequestDetail.individualMiddleName) && this.selectedRequestDetail.individualMiddleName.match(nameRegex)) {
				result.setMessage('individualMiddleName', 'Please enter a valid name.')
			}
			if (this.selectedRequestDetail.individualCity !== null && !isEmpty(this.selectedRequestDetail.individualCity)) {
				if (this.selectedRequestDetail.individualCity.match(nameRegex)) {
					result.setMessage('individualCity', 'Please enter a valid city.')
				} else if (this.selectedRequestDetail.individualCity.length > 28) {
					result.setMessage('individualCity', 'Max length is 28 characters.')
				}
			}
			if (this.selectedRequestDetail.individualPhoneNumber !== null && !isEmpty(this.selectedRequestDetail.individualPhoneNumber) && !this.selectedRequestDetail.individualPhoneNumber.match(/^[0-9]*$/)) {
				result.setMessage('individualPhoneNumber', 'Valid HomePhone must be non-whitespace')
			}
			if (this.selectedRequestDetail.individualPhoneNumber !== null && !isEmpty(this.selectedRequestDetail.individualPhoneNumber) && !isLength(this.selectedRequestDetail.individualPhoneNumber, { min: 10, max: 10 })) {
				result.setMessage('individualPhoneNumber', 'Valid HomePhone must be exactly 10 numbers.')
			}
			if (this.selectedRequestDetail.individualAddress1 !== null && !isEmpty(this.selectedRequestDetail.individualAddress1)) {
				if (this.selectedRequestDetail.individualAddress1.match(nameRegex)) {
					result.setMessage('individualAddress1', 'Please enter a valid address.')
				} else if (this.selectedRequestDetail.individualAddress1.length > 200) {
					result.setMessage('individualAddress1', 'Max length is 200 characters.')
				}
			}
			if (this.selectedRequestDetail.individualAddress2 !== null && !isEmpty(this.selectedRequestDetail.individualAddress2)) {
				if (this.selectedRequestDetail.individualAddress2.match(nameRegex)) {
					result.setMessage('individualAddress2', 'Please enter a valid address.')
				} else if (this.selectedRequestDetail.individualAddress2.length > 200) {
					result.setMessage('individualAddress2', 'Max length is 200 characters.')
				}
			}
			if (this.selectedRequestDetail.customerEntityFacilityId === 0) {
				result.setMessage('customerEntityFacilityId', 'Facility is required.')
			}

			if (this.selectedRequestDetail.individualSocialSecurityNumber === null || isEmpty(this.selectedRequestDetail.individualSocialSecurityNumber.trim())) {
				if (this.selectedRequestDetail.individualDateOfBirth === null || new Date(this.selectedRequestDetail.individualDateOfBirth) > new Date()) {
					result.setMessage('individualSocialSecurityNumber', 'Date Of Birth or Social Security Number is required')
					result.setMessage('individualDateOfBirth', 'Date Of Birth or Social Security Number is required')
				}
			}

			if (this.selectedRequestDetail.individualDateOfBirth === null) {
				if (this.selectedRequestDetail.individualSocialSecurityNumber === null || isEmpty(this.selectedRequestDetail.individualSocialSecurityNumber.trim())) {
					result.setMessage('individualDateOfBirth', 'Date Of Birth or Social Security Number is required')
				}
			}

			if (this.selectedRequestDetail.individualDateOfBirth !== null && new Date(this.selectedRequestDetail.individualDateOfBirth) > new Date()) {
				result.setMessage('individualDateOfBirth', 'Date of Birth value cannot be a date in the future.')
			}

			if (this.selectedRequestDetail.individualZip !== null && !isEmpty(this.selectedRequestDetail.individualZip) && !isLength(this.selectedRequestDetail.individualZip, { min: 5, max: 5 }) && !isLength(this.selectedRequestDetail.individualZip, { min: 9, max: 10 })) {
				result.setMessage('individualZip', 'Valid ZIP Code must be either 5 or 9 characters long.')
			}
			if (this.selectedRequestDetail.individualZip !== null && !isEmpty(this.selectedRequestDetail.individualZip) && !this.selectedRequestDetail.individualZip.match(/^(?:\d{5}(?:[-]\d{4})?|\d{9})$/)) {
				result.setMessage('individualZip', 'Valid ZIP Code should contain only numbers and hypen.')
			}
			if (this.selectedRequestDetail.individualSocialSecurityNumber !== null && !isEmpty(this.selectedRequestDetail.individualSocialSecurityNumber)) {
				if (!this.selectedRequestDetail.individualSocialSecurityNumber.match(/\b[0-9]{3}[-][0-9]{2}[-][0-9]{4}\b/) && !this.selectedRequestDetail.individualSocialSecurityNumber.match(/\b[0-9]{9}\b/)) {
					if (!this.selectedRequestDetail.individualSocialSecurityNumber!.match(/\b^[0-9]{4}$\b/)) {
						result.setMessage('individualSocialSecurityNumber', 'Valid SSN should contain either 9 numbers and/or 2 hypens.')
						result.setMessage('individualDateOfBirth', 'Date Of Birth or Social Security Number is required')
					} else {
						if (this.selectedRequestDetail.individualDateOfBirth === null) result.setMessage('individualDateOfBirth', 'Provided SSN is partial, so Date of Birth is also required')
					}
				}
			}
		}
		return result
	}
	@action saveOrUpdateCoverageDiscoveryRequestDetail(): Promise<ICoverageDiscoveryResponse> {
		this.selectedRequestDetails = this.selectedRequestDetail

		return this.CoverageDiscoveryRequestApiClient.saveRequestDetail(this.selectedRequestDetails).catch(error => {
			if (error.response !== null && typeof error.response !== 'undefined' && error.response.data.userMessage) {
				throw new Error(error.response.data.userMessage)
			} else {
				throw new Error('An error occurred when saving the request')
			}
		})
	}
	@action setSelectedRequestDetail(RequestDetail: ICoverageDiscoveryRequestDetail): void {
		this.selectedRequestDetail = RequestDetail
	}
	@action setAvailbleFacilitiesTransactionTypes(availableAccountFacilities: INpi[]): void {
		this.availableAccountFacilities = availableAccountFacilities
		this.setAvailbleFacilities(this.availableAccountFacilities)
	}
	@action setAvailbleFacilities(availableAccountFacilities: INpi[]): void {
		this.availableFacilities = []
		availableAccountFacilities.forEach(element => {
			this.availableFacilities.push(element)
		})
	}
	@action loadAvailableFacilities(): Promise<void> {
		return this.AccountFacilityApiClient.getAllNpis()
			.then(response => {
				this.setAvailbleFacilities(response)
			})
			.catch(() => {
				throw new Error('An error occurred when loading available facilities')
			})
	}
	@action setAvailableStates(availableStates: IStates[]): void {
		this.availableStates = availableStates
	}
	@action loadAvailableStates(): Promise<void> {
		return this.CoverageDiscoveryRequestApiClient.getAllStates()
			.then(response => this.setAvailableStates(response))
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when loading available states')
			})
	}
	@action setResponseAvailable(responseAvailable: boolean): void {
		this.isResponseAvailable = responseAvailable
	}
	@action loadCoverageDiscoveryTransactionStatusId(id: number): Promise<void> {
		return this.CoverageDiscoveryRequestApiClient.getCoverageDiscoveryTransactionStatusId(id)
			.then(response => {
				this.setResponseAvailable(response)
			})
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when loading the item')
			})
	}

	@action setUpdatedCoverageDiscoveryStatus(responseStatus: boolean): void {
		this.isCoverageDiscoveryStatusUpdated = responseStatus
	}
	@action updateCDStatusTimeout(id: number): Promise<void> {
		return this.CoverageDiscoveryRequestApiClient.updateCDStatusTimeout(id)
			.then(response => {
				this.setUpdatedCoverageDiscoveryStatus(response)
			})
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when loading the item')
			})
	}

	@action loadRequestDetail(id: number): Promise<ICoverageDiscoveryRequestDetail> {
		return this.CoverageDiscoveryRequestApiClient.getRequestDetail(id)
			.then(response => response)
			.catch(error => {
				console.log(error)
				throw new Error('An error occurred when loading the item')
			})
	}
}
